import classNames from 'classnames';
import React, { Fragment, memo, useEffect, useMemo, useState } from 'react';

import type { SegmentedControlProps } from './SegmentedControl.types';

import styles from './segmented-control.module.scss';

const ALPHA__SegmentedControl = ({
  callback = () => {},
  className,
  isMultiSelect = false,
  name = 'segmented-control',
  segments,
}: SegmentedControlProps) => {
  const selectedSegmentsInput = useMemo(
    () =>
      segments?.reduce((acc, curr) => {
        if (curr.selected) {
          acc.push(curr.value);
        }
        return acc;
      }, []) || [],
    [segments]
  );

  const initialSelectedSegments = useMemo(() => {
    if (!selectedSegmentsInput?.length) {
      return [];
    }

    return isMultiSelect
      ? selectedSegmentsInput
      : selectedSegmentsInput.slice(selectedSegmentsInput.length - 1);
  }, [selectedSegmentsInput, isMultiSelect]);

  const [selectedValues, setSelectedValues] = useState(initialSelectedSegments);

  const segmentsHash = useMemo(
    () => segments?.map((s) => s.selected).join() || '',
    [segments]
  );

  useEffect(() => {
    setSelectedValues(initialSelectedSegments);
  }, [segmentsHash, initialSelectedSegments]);

  if (!segments || !segments.length) {
    return;
  }

  const inputType = isMultiSelect ? 'checkbox' : 'radio';
  const containerRole = isMultiSelect ? 'group' : 'radiogroup';

  const updateSelectedValues = (value: string) => {
    const updatedValues = isMultiSelect
      ? selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value]
      : [value];

    setSelectedValues(updatedValues);
  };

  const onInputChange = (value: string) => {
    updateSelectedValues(value);
    callback(value);
  };

  return (
    <div
      aria-label={name}
      aria-multiselectable={isMultiSelect}
      aria-required={isMultiSelect ? false : true}
      className={classNames(styles.container, className)}
      key={`segmented-control-container-${name}`}
      role={containerRole}
      tabIndex={0}
    >
      {segments.map((segment, i) => {
        const { IconComponent, iconTitle, label, value } = segment;
        return (
          <Fragment key={`segmented-control-${value}-${name}`}>
            <input
              checked={selectedValues?.includes(value)}
              className={classNames([styles[`input${i}`]])}
              id={`${label}-${name}`}
              name={name}
              onChange={() => onInputChange(value)}
              role={isMultiSelect ? 'checkbox' : 'radio'}
              tabIndex={0}
              type={inputType}
              value={value}
            />
            <label
              className={classNames(styles.label)}
              htmlFor={`${label}-${name}`}
            >
              {label}
              {IconComponent && (
                <IconComponent
                  className={classNames(styles.icon)}
                  size="medium"
                  title={iconTitle}
                />
              )}
            </label>
          </Fragment>
        );
      })}
      <div className={styles.selectedIndicator}></div>
    </div>
  );
};

export default memo(ALPHA__SegmentedControl);
